<template>
    <div>
        <div v-if="loading" class="text-center">
            <i class="fa fa-circle-o-notch fa-spin"></i> Loading
        </div>

        <div v-else>
            <vue-headful :title="equipment_tracking_id ? 'Edit' : 'Create' + ' Equipment Tracking :: MeritEHS'"/>

            <h4 class="py-3 mb-4">
               <ol class="breadcrumb font-weight-bold m-0 d-block d-md-inline-block">
                    <li class="breadcrumb-item font-weight-light d-block d-md-inline-block">
                        <router-link :to="`/equipment`">
                            <i class="fa fa-truck"></i> Equipment
                        </router-link>
                    </li>
                    <li class="breadcrumb-item font-weight-light d-block d-md-inline-block mt-md-0 mt-2">
                        <router-link :to="`/equipment/view/${equipment.equipment_id}`">
                            {{ equipment.equipment_type_name }}
                            <span v-if="equipment.equipment_name">-</span> {{ equipment.equipment_name }}
                            <span v-if="equipment.model_number">-</span> {{ equipment.model_number }}
                            <span v-if="equipment.serial_number">-</span> {{ equipment.serial_number }}
                        </router-link>
                    </li>
                    <li class="breadcrumb-item active d-block d-md-inline-block mt-md-0 mt-2">
                        <span v-if="equipment_tracking.equipment_tracking_id">Edit Tracking</span>
                        <span v-else>
                            <span v-if="equipment_tracking.tracking_status == 'Checked In'">Check In</span>
                            <span v-else-if="equipment_tracking.tracking_status == 'Issued'">Check Out</span>
                            <span v-else>Add Tracking</span>
                        </span>
                    </li>
                </ol>
            </h4>

            <div class="row">
                <div class="col-lg-10 col-md-12 mb-4">
                    <div class="card">
                        <div class="card-header">
                            <strong>Tracking</strong>
                        </div>
                        <div class="card-body">
                            <div class="alert alert-warning mb-2" v-if="preventCheckout">
                                Equipment cannot be issued / checked out because there are open corrective actions tied to it. Complete these actions before checking out the equipment.
                            </div>

                            <div v-bind:class="{ 'form-group': true, 'has-error': errors.tracking_status }" class="row">
                                <label :class="{'text-danger': errors.tracking_status, 'col-lg-3 col-md-4 col-sm-5 col-form-label text-sm-right': true}">
                                    Action: <i class="fa fa-asterisk small text-danger"></i>
                                </label>
                                <div class="col-sm-8">
                                    <select v-model="equipment_tracking.tracking_status" class="form-control" :class="{'is-invalid': errors.tracking_status}">
                                        <template v-for="(action, key) in actions">
                                            <option v-if="key != initial_tracking_status" :value="key">
                                                {{ action }}
                                            </option>
                                        </template>
                                    </select>
                                    <span v-if="errors.tracking_status" class="text-danger">
                                        <span v-for="error in errors.tracking_status">{{ error }}</span>
                                    </span>
                                </div>
                            </div>

                            <div v-bind:class="{ 'form-group': true, 'has-error': errors.tracking_date }" class="row">
                                <label class="col-lg-3 col-md-4 col-sm-5 col-form-label text-sm-right">Tracking Date: <i class="fa fa-asterisk small text-danger"></i></label>
                                <div class="col-sm-8">
                                    <date-picker :value="readDate(equipment_tracking.tracking_date)" @input="equipment_tracking.tracking_date = writeDate($event)" :config="{format: 'MM/DD/YYYY h:mm a', useCurrent: false}" :class="{ 'is-invalid': errors.tracking_date }" placeholder=""></date-picker>
                                    <span v-if="errors.tracking_date" class="text-danger">
                                        <span v-for="error in errors.tracking_date">{{ error }}</span>
                                    </span>
                                </div>
                            </div>

                            <template v-if="equipment_tracking.tracking_status == 'Checked In'">
                                <div v-bind:class="{ 'form-group': true, 'has-error': errors.division_id }" class="row">
                                    <label class="col-lg-3 col-md-4 col-sm-5 col-form-label text-sm-right">Division: <i class="fa fa-asterisk small text-danger"></i></label>
                                    <div class="col-sm-8">
                                        <select v-model="equipment_tracking.division_id" class="form-control" :class="{'is-invalid': errors.division_id}">
                                            <option :value="null">
                                                -- Select a Division --
                                            </option>
                                            <option v-for="(division_name, division_id) in divisions" :value="division_id">
                                                {{ division_name }}
                                            </option>
                                        </select>
                                        <span v-if="errors.division_id" class="text-danger">
                                            <span v-for="error in errors.division_id">{{ error }}</span>
                                        </span>
                                    </div>
                                </div>
                            </template>

                            <template v-else-if="equipment_tracking.tracking_status == 'Issued'">
                                <template v-if="this.$root.hasPermission('equipment.manage')">
                                    <div v-bind:class="{ 'form-group': true, 'has-error': errors.employee_id }" class="row">
                                        <label :class="{'text-danger': errors.employee_id}" class="col-lg-3 col-md-4 col-sm-5 col-form-label text-sm-right">
                                            Assignee: <i class="fa fa-asterisk small text-danger"></i>
                                        </label>
                                        <div class="col-sm-8">
                                            <employee-select ref="equipTrackingEmp" :selectedValues="TrackingEmployee" @vselected="onEmployeeSelectUpdate" :hasError="errors.employee_id ? true : false" ></employee-select>

                                            <div v-if="this.$store.state.user.EmployeeId" class="mt-1 mb-1">
                                                <a href="javascript:void(0)" title="Assign this equipment to you" v-on:click="assignToMe()">Assign to Me</a>
                                            </div>

                                            <span v-if="errors.employee_id" class="text-danger">
                                                <span v-for="error in errors.employee_id">{{ error }}</span>
                                            </span>
                                        </div>
                                    </div>
                                </template>

                                <div v-bind:class="{ 'form-group': true, 'has-error': errors.jobsite_id }" class="row">
                                    <label :class="{'text-danger': errors.jobsite_id}"class="col-lg-3 col-md-4 col-sm-5 col-form-label text-sm-right">Jobsite: <i v-if="equipment_type.require_tracking_jobsite" class="fa fa-asterisk small text-danger"></i></label>
                                    <div class="col-sm-8">
                                        <jobsite-select :selectedValues="SiteSelectedValues" @vselected="onJobSiteSelectUpdate"></jobsite-select>
                                        <span v-if="errors.jobsite_id" class="text-danger">
                                            <span v-for="error in errors.jobsite_id">{{ error }}</span>
                                        </span>
                                    </div>
                                </div>
                            </template>

                            <template v-else-if="equipment_tracking.tracking_status == 'On Hold'">
                                <div v-bind:class="{ 'form-group': true, 'has-error': errors.division_id }" class="row">
                                    <label class="col-lg-3 col-md-4 col-sm-5 col-form-label text-sm-right">Division: <i class="fa fa-asterisk small text-danger"></i></label>
                                    <div class="col-sm-8">
                                        <select v-model="equipment_tracking.division_id" class="form-control">
                                            <option :value="null">
                                                -- Select a Division --
                                            </option>
                                            <option v-for="(division_name, division_id) in divisions" :value="division_id">
                                                {{ division_name }}
                                            </option>
                                        </select>
                                        <span v-if="errors.division_id" class="text-danger">
                                            <span v-for="error in errors.division_id">{{ error }}</span>
                                        </span>
                                    </div>
                                </div>
                            </template>

                            <template v-if="equipment_tracking.tracking_status !== 'On Hold'">
                                <div class="form-group row">
                                    <label :class="{'col-lg-3 col-md-4 col-sm-5 col-form-label text-sm-right': true, 'text-danger': (errors.inspection_file || errors.inspection_id)}">
                                        Add Inspection: <i v-if="(equipment_tracking.require_inspection && equipment_tracking.tracking_status == 'Checked In') || (equipment_tracking.require_insp_on_checkout && equipment_tracking.tracking_status == 'Issued')" class="fa fa-asterisk small text-danger"></i><br/>
                                        Or Link MeritEHS Inspection:
                                    </label>
                                    <div class="col-sm-8">

                                        <b-form-file v-model="newFile" class="mb-2" :state="errors.inspection_file || errors.inspection_id ? false : null" placeholder="Upload Inspection or Select an Inspection Below"></b-form-file>

                                        <div v-if="errors.inspection_file" class="text-danger mt-1 mb-2">
                                            <span v-for="error in errors.inspection_file">{{ error }}</span>
                                        </div>

                                        <inspection-select ref="inspSelector" @vselected="onInspSelect" :hasError="errors.inspection_id ? true : false" :filters="{equipment_id: equipment.equipment_id}"></inspection-select>

                                        <div v-if="errors.inspection_id" class="text-danger mt-1 mb-2">
                                            <span v-for="error in errors.inspection_id">{{ error }}</span>
                                        </div>

                                        <div class="mt-1 mb-1">
                                            <router-link :to="`/inspections/create?equipment_id=${equipment.equipment_id}`"><i class="fa fa-plus"></i> Create An Inspection</router-link>
                                        </div>

                                        <div v-if="inspections.length > 0" class="mt-3">
                                            <strong>Possible Inspections - Click to Select:</strong>
                                            <ul class="list-unstyled">
                                                <li v-for="insp in inspections" class="mb-1">
                                                    <a href="javascript:void(0)" v-on:click="setInsp(insp)">
                                                        INSP {{ insp.InspectionId }} on {{ insp.InspectionDate | moment("MM/DD/YYYY") }} @ {{ insp.JobsiteNumber }} - {{ insp.JobsiteName }}
                                                    </a>
                                                </li>
                                            </ul>
                                        </div>
                                    </div>
                                </div>
                            </template>

                            <div v-bind:class="{ 'form-group': true, 'has-error': errors.add_usage }" class="row">
                                <label class="col-lg-3 col-md-4 col-sm-5 col-form-label text-sm-right">Log Usage?:</label>
                                <div class="col-sm-8">
                                    <b-form-checkbox class="mt-2" v-model="equipment_tracking.add_usage" :value="1" :unchecked-value="0" plain />
                                    <span v-if="errors.add_usage" class="text-danger">
                                        <span v-for="error in errors.add_usage">{{ error }}</span>
                                    </span>
                                </div>
                            </div>

                            <div class="form-group row">
                                <label :class="{'col-lg-3 col-md-4 col-sm-5 col-form-label text-sm-right': true, 'text-danger': errors.notes}">Notes:</label>
                                <div class="col-sm-8">

                                    <b-form-textarea v-model="equipment_tracking.notes" :class="{'is-invalid': errors.notes}" rows="4" placeholder="(Optional) Enter Notes"></b-form-textarea>

                                    <span v-if="errors.notes" class="text-danger">
                                        <span v-for="error in errors.notes">{{ error }}</span>
                                    </span>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div v-if="equipment_tracking.add_usage == 1" class="card mt-4">
                        <div class="card-header">
                            <strong>Logged Usage</strong>
                        </div>
                        <div class="card-body">
                            <div v-bind:class="{ 'form-group': true, 'has-error': errors.amount }" class="row">
                                <label v-if="equipment_type.use_absolute_usage" class="col-lg-3 col-md-4 col-sm-5 col-form-label text-sm-right">Current Usage: <i class="fa fa-asterisk small text-danger"></i></label>
                                <label v-else class="col-lg-3 col-md-4 col-sm-5 col-form-label text-sm-right">Logged Usage: <i class="fa fa-asterisk small text-danger"></i></label>
                                <div class="col-sm-8">
                                    <span class="input-group">
                                        <input type="number" name="amount" v-model="equipment_tracking.amount" class="form-control" :placeholder="'Enter Amount' + (equipment_tracking.equipment_usage_type_id ? ` of ${equipment_usage_types[equipment_tracking.equipment_usage_type_id]}` : '')" />
                                        <div v-if="equipment_tracking.equipment_usage_type_id" class="input-group-append">
                                            <span class="input-group-text bg-light">{{equipment_usage_types[equipment_tracking.equipment_usage_type_id]}}</span>
                                        </div>
                                    </span>

                                    <span v-if="errors.amount" class="text-danger">
                                        <span v-for="error in errors.amount">{{ error }}</span>
                                    </span>
                                </div>
                            </div>

                            <div v-bind:class="{ 'form-group': true, 'has-error': errors.equipment_usage_type_id }" class="row">
                                <label class="col-lg-3 col-md-4 col-sm-5 col-form-label text-sm-right">Usage Type: <i class="fa fa-asterisk small text-danger"></i></label>
                                <div class="col-sm-8">
                                    <select name="equipment_usage_type_id" v-model="equipment_tracking.equipment_usage_type_id" class="form-control">
                                        <option :value="null">-- Select a Usage Type --</option>
                                        <option v-for="(type, value) in equipment_usage_types" :value="value">{{type}}</option>
                                    </select>

                                    <span v-if="errors.equipment_usage_type_id" class="text-danger">
                                        <span v-for="error in errors.equipment_usage_type_id">{{ error }}</span>
                                    </span>
                                </div>
                            </div>

                            <div class="form-group row">
                                <label :class="{'col-lg-3 col-md-4 col-sm-5 col-form-label text-sm-right': true, 'text-danger': errors.description}">Description:</label>
                                <div class="col-sm-8">

                                    <b-form-textarea v-model="equipment_tracking.description" :class="{'is-invalid': errors.description}" rows="4" placeholder="(Optional) Enter Description"></b-form-textarea>

                                    <span v-if="errors.description" class="text-danger">
                                        <span v-for="error in errors.description">{{ error }}</span>
                                    </span>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <hr/>
            <button type="button" class="btn btn-success" v-on:click="saveEquipmentTracking()" :disabled="saving || preventCheckout">
                Update Equipment Tracking
            </button>
        </div>
    </div>
</template>


<script>
    import moment from 'moment-timezone';

    export default {
        data: function() {
            return {
                loading: true,
                saving: false,
                errors: {},
                initial_tracking_status: null,
                equipment: {},
                equipment_id: this.$route.params.equipment_id,
                equipment_tracking_id: this.$route.params.id,
                equipment_tracking: {},
                equipment_usage_types: {},
                equipment_type: {},
                actions: {},
                divisions: {},
                inspections: [],
                newFile: null,
                assignee_types: ['User', 'Employee'],
                JobsiteId: 0,
                options: {
                    format: 'MM/DD/YYYY',
                    date: ""
                },
            }
        },

        computed: {
            SiteSelectedValues(){
                //check for null
                if(!this.equipment_tracking.jobsite_id || this.equipment_tracking.jobsite_id == null)
                {
                    //Check the store
                    if (this.equipment.jobsite_id)
                    {
                        this.equipment_tracking.jobsite_id = this.equipment.jobsite_id;

                        return {JobsiteId: this.equipment.jobsite_id, JobsiteNumber: this.equipment.JobsiteNumber, JobsiteName: this.equipment.JobsiteName};
                    }
                    else if(this.$store.state.user.jobsite)
                    {
                        var jobsite = this.$store.state.user.jobsite;

                        //Set the HSE jobsite
                        this.equipment_tracking.jobsite_id = jobsite.JobsiteId;

                        //Send it
                        return {JobsiteId: jobsite.JobsiteId, JobsiteNumber: jobsite.JobsiteNumber, JobsiteName: jobsite.JobsiteName};
                    }

                    //Send it
                    return null;
                }

                //Send it
                return {JobsiteId: this.equipment_tracking.jobsite_id, JobsiteNumber: this.equipment_tracking.JobsiteNumber, JobsiteName: this.equipment_tracking.JobsiteName};
            },

            TrackingEmployee(){
                //check for null
                if(!this.equipment_tracking.assignee_id)
                {
                    return {};
                }

                return {EmployeeId: this.equipment_tracking.assignee_id, FirstName: this.equipment_tracking.FirstName, LastName: this.equipment_tracking.LastName, EmployeeNumber: this.equipment_tracking.EmployeeNumber};
            },

            preventCheckout(){
                //Check all params
                if(this.equipment_tracking.tracking_status == 'Issued' && this.openActions.length > 0 && this.equipment_tracking.require_insp_on_checkout == 1)
                {
                    return true;
                }

                //Default
                return false;
            },
        },

        methods: {
            // Convert database date to human
            readDate(date) {
                return moment(date, 'YYYY-MM-DD hh:mm:ss').format('MM/DD/YYYY h:mm a');
            },

            // Convert human date to database
            writeDate(date) {
                return moment(date, 'MM/DD/YYYY h:mm a').format('YYYY-MM-DD hh:mm:ss');
            },

            getEquipmentTracking() {
                //Get the data
                this.$http
                    .get(`equipment/tracking/${this.equipment_id}/edit` + (this.equipment_tracking_id ? `/${this.equipment_tracking_id}` : ''))
                    .then(response => {
                        //Set all the data
                        this.equipment_tracking = response.data.tracking;
                        this.divisions = response.data.divisions;
                        this.actions = response.data.actions;
                        this.equipment = response.data.equipment;
                        this.initial_tracking_status = this.equipment.current_tracking_status;
                        this.equipment_usage_types = response.data.equipment_usage_types;
                        this.equipment_type = response.data.equipment_type;
                        this.inspections = response.data.inspections;
                        this.openActions = response.data.openActions;

                        //Set loading
                        this.loading = false;
                    })
                    .catch(err => {
                        //Log the error
                        console.log(err);
                    });
            },

            saveEquipmentTracking() {
                //SHow loading
                this.saving = true;

                //Reset the errors
                this.errors = {};

                //Send it
                this.$http
                    .postWithSingleFile(`equipment/tracking/${this.equipment_id}/edit` + (this.equipment_tracking_id ? `/${this.equipment_tracking_id}` : ''), this.equipment_tracking, this.newFile)
                    .then(response => {
                        //Alert the user?
                        this.$toastr.success('Equipment Hours Saved');

                        //Return to main list?
                        this.$router.push(`/equipment/view/${this.equipment_id}`);
                    })
                    .catch(error => {
                        //Check for error
                        if (error.response.status === 422) {
                            //Display validation errors
                            this.errors = error.response.data
                        }
                    })
                    .then(response => {
                        //SHow loading
                        this.saving = false;
                    });
            },

            onJobSiteSelectUpdate (jobsite) {
                //Check for null
                if(jobsite == null)
                {
                    //Stop
                    this.equipment_tracking.jobsite_id = null;
                    return;
                }

                //Send the value
                this.equipment_tracking.jobsite_id = jobsite.JobsiteId;
            },

            onUserSelectUpdate ( user ) {
                this.$set(this.equipment_tracking, 'assignee_id', user.UserId);
            },

            onEmployeeSelectUpdate ( employee ) {
                //this.$set(this.equipment_tracking, 'assignee_id', employee.EmployeeId);
                this.$set(this.equipment_tracking, 'employee_id', employee.EmployeeId);
            },

            setInsp(insp){
                //Set the value
                this.$refs.inspSelector.setSelectedValues(insp);

                //Set the ID?
                this.equipment_tracking.inspection_id = insp.InspectionId;
            },

            onInspSelect(inspection){
                //Check for null
                if (inspection == null)
                {
                    //Clear it
                    this.equipment_tracking.inspection_id = null;

                    //Stop here
                    return;
                }

                //Set the value
                this.equipment_tracking.inspection_id = inspection.InspectionId;
            },

            assignToMe(){
                //Set it
                this.equipment_tracking.FirstName = this.$store.state.user.first_name;
                this.equipment_tracking.LastName = this.$store.state.user.last_name;
                this.equipment_tracking.EmployeeNumber = this.$store.state.user.EmployeeNumber;
                this.equipment_tracking.assignee_id = this.$store.state.user.EmployeeId;
                this.$set(this.equipment_tracking, 'employee_id', this.$store.state.user.EmployeeId);

                //Update
                this.$forceUpdate();
            },
        },

        mounted() {
            this.getEquipmentTracking();
        },

    }
</script>
<style>
    .form-inline .multiselect {
        display: inline-block;
        width: auto;
    }
</style>